"use client"

import { ShieldCheck, Globe2, KeyRound, Check } from "lucide-react"
import type { ShareableSkill } from "@/lib/community/publish-helpers"

const PRIVACY_ICON: Record<string, typeof ShieldCheck> = {
  local: ShieldCheck,
  "local-optional": ShieldCheck,
  external: Globe2,
  "auth-required": KeyRound,
  "security-tool": ShieldCheck,
}

const PRIVACY_COLOR: Record<string, string> = {
  local: "text-emerald-500",
  "local-optional": "text-emerald-500",
  external: "text-sky-500",
  "auth-required": "text-sky-500",
  "security-tool": "text-blue-500",
}

interface PublishSkillPickerProps {
  skills: ShareableSkill[]
  selectedIds: Set<string>
  onToggle: (id: string) => void
  scenarioTitle: string
}

export function PublishSkillPicker({
  skills,
  selectedIds,
  onToggle,
  scenarioTitle,
}: PublishSkillPickerProps) {
  const count = selectedIds.size

  return (
    <div className="space-y-2.5">
      {/* Dynamic copy */}
      <p className="text-caption text-foreground/70 leading-relaxed">
        {count === 0
          ? `选择你想推荐的 Skills — 来自「${scenarioTitle}」`
          : `已选 ${count} 个 Skill，推荐给有同样需求的用户`}
      </p>

      {/* Skill list */}
      <div className="space-y-1.5">
        {skills.map((skill) => {
          const isSelected = selectedIds.has(skill.id)
          const Icon = PRIVACY_ICON[skill.privacy] ?? ShieldCheck
          const colorClass = PRIVACY_COLOR[skill.privacy] ?? "text-muted-foreground"

          return (
            <button
              key={skill.id}
              type="button"
              onClick={() => onToggle(skill.id)}
              className={`w-full flex items-start gap-2.5 rounded-lg px-3 py-2 text-left transition-all duration-150 ${
                isSelected
                  ? "bg-blue-500/8 border border-blue-500/25 ring-1 ring-blue-500/10"
                  : "bg-secondary/20 border border-transparent hover:bg-secondary/40"
              }`}
            >
              {/* Checkbox */}
              <div
                className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors ${
                  isSelected
                    ? "bg-blue-500 border-blue-500"
                    : "border-border/60 bg-secondary/30"
                }`}
              >
                {isSelected && <Check className="h-2.5 w-2.5 text-white" />}
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <Icon className={`h-3 w-3 shrink-0 ${colorClass}`} />
                  <span className="text-xs font-medium text-foreground truncate">
                    {skill.name}
                  </span>
                  {skill.installed && (
                    <span className="text-nano bg-emerald-500/10 text-emerald-500 px-1 rounded">
                      已安装
                    </span>
                  )}
                </div>
                <p className="text-micro text-muted-foreground mt-0.5 line-clamp-1">
                  {skill.role}
                </p>
              </div>
            </button>
          )
        })}
      </div>

      {skills.length === 0 && (
        <p className="text-caption text-muted-foreground/50 text-center py-4">
          该场景暂无 Skill 数据
        </p>
      )}
    </div>
  )
}
